import { create } from 'zustand';
import {
    fetchFilteredOpportunities,
    fetchOpportunitiesStats,
} from '../service/opportunity-service';
import {
    DESKTOP_LIMIT,
    DEFAULT_DATA,
    OPPORTUNITY_STATUS,
    OPPORTUNITY_BOND_TYPES,
} from '../utils/constants';
import { RegulationTypes } from '@helpers/enums/regulation-types';
import useAuthStore from '@store/useAuthStore';
import { getCachedData } from '../utils/utils';

const useOpportunitiesStore = create((set, get) => {
    useAuthStore.subscribe((state) => {
        const isAuthorized = state.isLogged && state.authenticated;
        set({ isAuthorized: isAuthorized ? true : false });
    });
    return {
        data: DEFAULT_DATA,
        error: null,
        isFetching: true,
        showSortModal: false,
        showFilterModal: false,
        status: OPPORTUNITY_STATUS.OPEN,
        page: 1,
        stats: null, // Store opportunity stats

        isAuthorized:
            useAuthStore.getState().isLogged &&
            useAuthStore.getState().authenticated,

        setStatus: (status) => {
            set({
                status,
                data: DEFAULT_DATA,
                page: 1,
            });
        },

        openSortModal: () => set({ showSortModal: true }),
        closeSortModal: () => set({ showSortModal: false }),
        openFilterModal: () => set({ showFilterModal: true }),
        closeFilterModal: () => set({ showFilterModal: false }),

        /**
         * Fetch cached or fresh opportunity count stats.
         * Uses sessionStorage to prevent unnecessary API calls.
         */
        fetchOpportunitiesStats: async (showActiveOpportunities) => {
            try {
                const status = showActiveOpportunities
                    ? OPPORTUNITY_STATUS.OPEN
                    : OPPORTUNITY_STATUS.CLOSED;

                const { cachedData, storageKey } = getCachedData(
                    get().isAuthorized,
                    showActiveOpportunities
                );

                if (
                    cachedData &&
                    new Date().getTime() - cachedData.timestamp <
                        process.env.NEXT_PUBLIC_OPPORTUNITY_STATS_CACHE_DURATION
                ) {
                    return set({ stats: cachedData.data });
                }

                const opportunitiesStats = await fetchOpportunitiesStats({
                    status,
                });

                if (opportunitiesStats) {
                    set({ stats: opportunitiesStats });

                    sessionStorage.setItem(
                        storageKey,
                        JSON.stringify({
                            data: opportunitiesStats,
                            timestamp: new Date().getTime(),
                        })
                    );
                }
            } catch (error) {
                console.error('❌ Error fetching opportunity stats:', error);
                set({ stats: null });
            }
        },

        fetchOpportunities: async (queryParams) => {
            const { regulation, ...filteredQueryParams } = queryParams;
            const {
                type = OPPORTUNITY_BOND_TYPES.BOND,
                status = get().status,
                page = 1,
                limit = window.innerWidth < 1280 ? MOBILE_LIMIT : DESKTOP_LIMIT,
            } = filteredQueryParams;

            try {
                set({
                    isFetching: true,
                    error: null,
                    data: page === 1 ? DEFAULT_DATA : get().data,
                });

                const response = await fetchFilteredOpportunities({
                    ...filteredQueryParams,
                    type:
                        type === OPPORTUNITY_BOND_TYPES.UNLISTEDSDI ||
                        type === OPPORTUNITY_BOND_TYPES.SDIS
                            ? regulation === RegulationTypes.SEBI
                                ? OPPORTUNITY_BOND_TYPES.SDIS
                                : OPPORTUNITY_BOND_TYPES.UNLISTEDSDI
                            : type,
                    status,
                    page,
                    limit,
                });

                const updatePageData = (state, response, page) => {
                    // Check if the page is the first one
                    if (page === 1) {
                        // If the status matches, return the new response, else return the current data
                        return response.status === state.status
                            ? response
                            : state.data;
                    } else {
                        // If the existing data's type matches and the status matches, append the new data to old data
                        if (
                            state.data.rows[0]?.type ===
                                response.rows[0]?.type &&
                            response?.status === state.status
                        ) {
                            return {
                                rows: [...state.data.rows, ...response.rows],
                                count: response.count,
                            };
                        } else {
                            return state.data;
                        }
                    }
                };

                set((state) => ({
                    page,
                    data: updatePageData(state, response, page),
                }));
            } catch (error) {
                console.error('❌ Error fetching opportunities:', error);
                set({
                    error: {
                        error: error?.message,
                        type,
                        page,
                    },
                });
            } finally {
                set({ isFetching: false });
            }
        },
    };
});

export default useOpportunitiesStore;
